import React, { useEffect, useMemo, useState } from 'react';
import DbbSegment, { IDbbSegment } from '../../components/DbbSegment';
import { CheckChangeContext, Radio } from 'tea-component';
import { selectValByCondition } from '@home/utils/common';
import classNames from 'classnames';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { BaseComponentProps } from '../../types/commom';
import { buriedBtnPoint } from '@home/utils/buriedPoint';

export interface IRadioOption {
  // 选项值
  value: string;
  // 文本内容
  textEn: string;
  textCn: string;
  // 禁用
  disabled?: boolean;
  // 浮窗提示
  tooltipEn?: string;
  tooltipCn?: string;
}

export interface IRadioProps extends BaseComponentProps {
  // 选项
  options: IRadioOption[];
  // 默认值
  defaultValue?: string;
  // 受控值
  value?: string;
  onChange?: (value: string, context: CheckChangeContext) => void;
}

export interface ITabsAndRadioProps extends React.PropsWithChildren {
  // tab选项
  tabsOptions: IDbbSegment;
  // 单选选项
  radioOptions: IRadioProps;
  logConfig?: {
    // 按钮id
    buttonId?: string;
    // 页面id
    pageId?: string;
    // 框架类型id
    typeId?: string;
    // 打点信息
    extInfo?: { [key: string]: any; };
  };
  [key: string]: any;
}

const TabsAndRadio = (props: ITabsAndRadioProps) => {
  const {
    tabsOptions,
    radioOptions,
    children,
    logConfig,
    ...resetProps
  } = props;
  const {
    style = {},
    className,
    options,
    defaultValue,
    value: propsRadioValue,
    onChange,
  } = radioOptions || {};

  // 默认拿第一个单选值
  const [radioValue, setRadioValue] = useState<string>(
    defaultValue || options?.[0]?.value,
  );

  // 受控值
  useEffect(() => {
    if (propsRadioValue) {
      setRadioValue(propsRadioValue);
    }
  }, [propsRadioValue]);

  const { isEN } = useLocal();

  // 按钮打点
  const setBtnPoint = (tabValue: string, radioValue: string) => {
    const { buttonId, pageId, typeId, extInfo } = logConfig || {};
    const { hideAfterNodeByTabKeys = [] } = tabsOptions || {};
    const hide = hideAfterNodeByTabKeys?.length > 0 && hideAfterNodeByTabKeys?.includes(tabValue);
    if (buttonId && pageId) {
      const info = { ...(extInfo || { filters: resetProps.filterVal } || {}), tabValue, ...(hide ? {} : { radioValue }) };
      const logObj = {
        buttonId,
        extInfo: JSON.stringify(info),
        pageId,
        uidType: '', // 基准打点不包含uidType
        typeId,
      };
      buriedBtnPoint(logObj);
    }
  };

  // 按钮打点打点
  const onTabsChange = (value: string) => {
    tabsOptions?.onChange?.(value);
    setBtnPoint(value, radioValue);
  };

  const radioDom = (tabValue: string) => <Radio.Group
    value={radioValue}
    onChange={(value, context) => {
      // 解决tea组件bug，重复点击还会触发change事件
      if (value === radioValue) {
        return;
      }
      onChange?.(value, context);
      setBtnPoint(tabValue, value);
      setRadioValue(value);
    }}
    style={{ marginLeft: 20, ...style }}
    className={classNames(className)}
  >
    {options?.map((item) => {
      const { value, textCn, textEn, disabled, tooltipEn = '', tooltipCn = '' } =
        item;
      return (
        <Radio
          key={value}
          name={value}
          disabled={disabled}
          tooltip={selectValByCondition(isEN, tooltipEn, tooltipCn)}
        >
          {selectValByCondition(isEN, textEn, textCn)}
        </Radio>
      );
    })}
  </Radio.Group>;

  const activeRadio = useMemo(
    () => options?.find((item) => item.value === radioValue),
    [radioValue, options],
  );

  return (
    <DbbSegment
      {...(tabsOptions || {})}
      afterNode={radioDom}
      onChange={onTabsChange}
    >
      {React.Children.map(children, (child: any) =>
        React.cloneElement(child, {
          ...resetProps,
          activeRadio,
        }),
      )}
    </DbbSegment>
  );
};

export default TabsAndRadio;
